/**
 * @module CaseAnalysisTab
 * @purpose Analysis tab container — routes between hypotheses, canvas, AI workbench, bookmarks, scripts, briefing board.
 * @depends CanvasView, AiWorkbenchStub, BookmarksStub, SkillsScriptsStub, BriefingBoardView, HypothesesView, AnalysisTabBar
 * @dependedOnBy CaseDetailPage (or equivalent case container)
 *
 * @rules
 * - Performance: sub-tab switching is purely local state
 * - State: active sub-tab in local useState
 * - Errors: n/a — delegated to child components
 */

import { useState, useCallback } from 'react'
import { AnalysisTabBar } from './analysis/AnalysisTabBar'
import { CanvasView } from './analysis/CanvasView'
import { AiWorkbenchStub } from './analysis/AiWorkbenchStub'
import { BookmarksStub } from './analysis/BookmarksStub'
import { SkillsScriptsStub } from './analysis/SkillsScriptsStub'
import { BriefingBoardView } from './analysis/reasoning/briefing/BriefingBoardView'
import { HypothesesView } from './analysis/reasoning/hypothesis/HypothesesView'
import type { AnalysisSubTab } from './analysis/types'

interface CaseAnalysisTabProps {
  caseId: string
}

function renderSubTabContent(
  tab: AnalysisSubTab,
  caseId: string,
  onViewBriefing: () => void,
): React.ReactElement {
  switch (tab) {
    case 'hypotheses': {
      return <HypothesesView caseId={caseId} onViewBriefing={onViewBriefing} />
    }
    case 'briefingBoard': {
      return <BriefingBoardView caseId={caseId} />
    }
    case 'aiWorkbench': {
      return <AiWorkbenchStub />
    }
    case 'bookmarks': {
      return <BookmarksStub />
    }
    case 'skillsScripts': {
      return <SkillsScriptsStub />
    }
    case 'canvas': {
      return <CanvasView caseId={caseId} />
    }
  }
}

/**
 * Analysis tab switcher for a case.
 *
 * @behaviour Forwards caseId to the active sub-tab view.
 *   Passes a onViewBriefing callback to HypothesesView so it can switch to the briefing board.
 * @errors n/a — delegated to child components
 */
export function CaseAnalysisTab({ caseId }: CaseAnalysisTabProps): React.ReactElement {
  const [activeSubTab, setActiveSubTab] = useState<AnalysisSubTab>('canvas')

  const handleTabChange = useCallback((tab: AnalysisSubTab) => {
    setActiveSubTab(tab)
  }, [])

  const handleViewBriefing = useCallback(() => {
    setActiveSubTab('briefingBoard')
  }, [])

  return (
    <div className="flex flex-col gap-0">
      <AnalysisTabBar activeTab={activeSubTab} onTabChange={handleTabChange} />
      <div role="tabpanel" className="mt-4">{renderSubTabContent(activeSubTab, caseId, handleViewBriefing)}</div>
    </div>
  )
}
